/**
 * HomeAgentMarketplaceStrip.jsx
 *
 * Compact "Meet your AI workforce" strip that lives on the homepage
 * BETWEEN the HomepageLossHero and the PulseAI engine showcase. Goal:
 * surface the 5 Agent OS agents to every visitor who lands on toprankpulse.com
 * so they discover them without needing to navigate to /agent-os/eshop.
 *
 * Design choice — strip, not full section:
 *   The homepage already has a lot of conversion surfaces ($9 Snapshot,
 *   Pro plan calculator, Pulse AI Engine). A full hero would compete with
 *   them. Keeping this as a tight horizontal strip with 1-line per agent
 *   + price chip + "Try demo" link.
 */
import { MessageSquare, ShoppingBag, FileText, Mail, ShieldCheck, ArrowRight, Bot, Share2, TrendingUp, UserPlus, ShieldAlert, BarChart3 } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const COPY = {
  en: {
    tag: "Meet your AI workforce",
    h2: "Hire your first AI agent in 60 seconds",
    sub: "11 always-on agents that replace €2 000+/mo of human work. Try any demo — no signup.",
    cta_all: "All agents →",
    try: "Try demo →",
  },
  sk: {
    tag: "Spoznaj svoju AI pracovnú silu",
    h2: "Najmi si prvého AI agenta za 60 sekúnd",
    sub: "11 always-on agentov, ktorí nahradia 2 000+ € / mes ľudskej práce. Skús ktorékoľvek demo — bez registrácie.",
    cta_all: "Všetci agenti →",
    try: "Skús demo →",
  },
  cs: {
    tag: "Poznej svou AI pracovní sílu",
    h2: "Najmi si prvního AI agenta za 60 sekund",
    sub: "5 always-on agentů, kteří nahradí 1 500–1 800 € / mes lidské práce. Vyzkoušej jakékoli demo — bez registrace.",
    cta_all: "Všichni agenti →",
    try: "Zkus demo →",
  },
  de: {
    tag: "Triff deine KI-Belegschaft",
    h2: "Stelle deinen ersten KI-Agenten in 60 Sekunden ein",
    sub: "5 immer aktive Agenten, die €1 500–1 800 / Monat menschliche Arbeit ersetzen. Probier jede Demo — keine Anmeldung.",
    cta_all: "Alle Agenten →",
    try: "Demo ausprobieren →",
  },
  pl: {
    tag: "Poznaj swoją AI siłę roboczą",
    h2: "Zatrudnij pierwszego AI agenta w 60 sekund",
    sub: "5 zawsze aktywnych agentów zastępujących 1 500–1 800 € / mes ludzkiej pracy. Wypróbuj każde demo — bez rejestracji.",
    cta_all: "Wszyscy agenci →",
    try: "Wypróbuj demo →",
  },
  hu: {
    tag: "Ismerd meg az AI munkaerődet",
    h2: "Vegyél fel első AI ügynököd 60 másodperc alatt",
    sub: "5 mindig aktív ügynök, akik 1 500–1 800 € / hó emberi munkát váltanak ki. Próbáld ki bármelyik demót — regisztráció nélkül.",
    cta_all: "Minden ügynök →",
    try: "Demo kipróbálása →",
  },
};

const AGENTS = [
  {
    id: "helpdesk", icon: MessageSquare,
    name_en: "Helpdesk Agent", name_sk: "Helpdesk Agent",
    saves_en: "Replaces 1× CS rep (€1 800/mo)", saves_sk: "Nahradí 1× CS agenta (€1 800/mes)",
    price: "€99/mo", demo: "/agent-os/demo/helpdesk", color: "violet",
  },
  {
    id: "review_reply", icon: MessageSquare,
    name_en: "Review Reply", name_sk: "Review Reply",
    saves_en: "30 min × 50 reviews/mo", saves_sk: "30 min × 50 recenzií / mes",
    price: "€79/mo", demo: "/agent-os/demo/review-reply", color: "emerald",
  },
  {
    id: "cart_recovery", icon: ShoppingBag,
    name_en: "Cart Recovery", name_sk: "Cart Recovery",
    saves_en: "12-18% recovery rate", saves_sk: "12-18% recovery rate",
    price: "€99/mo", demo: "/agent-os/demo/cart-recovery", color: "amber",
  },
  {
    id: "product_desc", icon: FileText,
    name_en: "Product Desc Optimizer", name_sk: "Product Desc Optimizer",
    saves_en: "SEO + AI Search traffic", saves_sk: "SEO + AI Search traffic",
    price: "€79/mo", demo: "/agent-os/demo/product-desc", color: "blue",
  },
  {
    id: "newsletter", icon: Mail,
    name_en: "Newsletter Marketing", name_sk: "Newsletter Marketing",
    saves_en: "Replaces 1× SMM (€1 500)", saves_sk: "Nahradí 1× SMM (€1 500)",
    price: "€79/mo", demo: "/agent-os/demo/newsletter", color: "rose",
  },
  {
    id: "gdpr_watch", icon: ShieldCheck,
    name_en: "GDPR + A11y Watch", name_sk: "GDPR + A11y Watch",
    saves_en: "Daily compliance scan", saves_sk: "Denný compliance scan",
    price: "€49/mo", demo: "/agent-os/demo/gdpr-watch", color: "violet",
  },
  {
    id: "social_media", icon: Share2,
    name_en: "Social Media Scheduler", name_sk: "Social Media Scheduler",
    saves_en: "Replaces 1× SMM junior (€1 000/mo)", saves_sk: "Nahradí 1× SMM juniora (€1 000/mes)",
    price: "€79/mo", demo: "/agent-os/demo/social-media", color: "rose",
  },
  {
    id: "pricing_intel", icon: TrendingUp,
    name_en: "Pricing Intelligence", name_sk: "Pricing Intelligence",
    saves_en: "5-12% margin lift", saves_sk: "5-12% nárast margin",
    price: "€149/mo", demo: "/agent-os/demo/pricing-intel", color: "emerald",
  },
  {
    id: "customer_onboarding", icon: UserPlus,
    name_en: "Customer Onboarding", name_sk: "Customer Onboarding",
    saves_en: "+25% repeat purchase", saves_sk: "+25% opakované nákupy",
    price: "€69/mo", demo: "/agent-os/demo/customer-onboarding", color: "blue",
  },
  {
    id: "review_moderator", icon: ShieldAlert,
    name_en: "Review Moderator", name_sk: "Review Moderator",
    saves_en: "Detects fake reviews + sabotage", saves_sk: "Detekuje fake recenzie + sabotáž",
    price: "€69/mo", demo: "/agent-os/demo/review-moderator", color: "amber",
  },
  {
    id: "behavior_analytics", icon: BarChart3,
    name_en: "Behavior Analytics", name_sk: "Behavior Analytics",
    saves_en: "Weekly AI fix suggestions", saves_sk: "Týždenné AI návrhy opráv",
    price: "€129/mo", demo: "/agent-os/demo/behavior-analytics", color: "violet",
  },
];

export default function HomeAgentMarketplaceStrip() {
  const { language } = useLanguage();
  const C = COPY[language] || COPY.en;
  const lang = COPY[language] ? language : "en";

  return (
    <section
      className="py-20 px-6 bg-[#0a0a0a] border-y border-emerald-900/30"
      data-testid="home-agent-marketplace-strip"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-emerald-400 font-['JetBrains_Mono'] text-xs tracking-[0.3em] uppercase mb-3 flex items-center justify-center gap-2">
            <Bot className="w-3 h-3" /> {C.tag}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter text-white mb-3">
            {C.h2}
          </h2>
          <p className="text-base text-gray-400 max-w-2xl mx-auto">{C.sub}</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4 mb-8">
          {AGENTS.map((a) => {
            const Icon = a.icon;
            const name = lang === "sk" ? a.name_sk : a.name_en;
            const saves = lang === "sk" ? a.saves_sk : a.saves_en;
            return (
              <a
                key={a.id}
                href={a.demo || "/agent-os/eshop"}
                data-testid={`home-agents-strip-${a.id}`}
                className="group p-5 bg-black border border-gray-800 hover:border-emerald-700/60 transition-all hover:translate-y-[-2px] flex flex-col"
              >
                <div className="flex items-start justify-between mb-3">
                  <Icon className="w-6 h-6 text-emerald-400" />
                  <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 tabular-nums">
                    {a.price}
                  </span>
                </div>
                <h3 className="text-base font-black text-white mb-1">{name}</h3>
                <p className="text-xs text-gray-400 mb-3 flex-1">{saves}</p>
                <span className="text-[10px] uppercase tracking-widest text-emerald-300 group-hover:text-emerald-200 font-bold">
                  {a.demo ? C.try : "Onedlho"}
                </span>
              </a>
            );
          })}
        </div>

        <div className="text-center">
          <a
            href="/agent-os/eshop"
            data-testid="home-agents-strip-all-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-400 hover:bg-emerald-300 text-black font-black uppercase tracking-widest text-sm"
          >
            {C.cta_all} <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
